/**
 * Sidebar — left navigation rail.
 *
 * Variants:
 *   'full'  — brand, labelled nav items, user block (default, 240px)
 *   'icons' — slim 76px rail: icon with a tiny label underneath,
 *             avatar-only user block. Tooltips carry the full labels.
 *
 * The variant only affects presentation — nav structure and role logic
 * are identical in both.
 *
 * Sizing: the aside fills its parent (h-full). On desktop the parent is
 * the sticky AppShell column; on mobile it's the slide-over drawer. The
 * nav body is the only scrollable region (min-h-0 + overflow-y-auto), so
 * brand row and user block stay pinned while long item lists scroll.
 *
 * Colors come from the tone-aware --sb-* variables (index.css) so the
 * rail renders correctly in dark mode and on light custom sidebar colors.
 */

import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.jsx';
import { getNavItems } from './RoleAwareNav.jsx';
import { getRolePrefix, roleLabel } from '../../utils/roleUtils.js';
import { APP } from '../../data/constants.js';
import Icon from '../ui/Icons.jsx';
import Avatar from '../ui/Avatar.jsx';

function NavItemRow({ item }) {
  // Disabled items render as plain divs, not links.
  if (item.disabled) {
    return (
      <div
        className="flex items-center gap-2.5 px-2.5 py-2 rounded-md text-sm font-medium orin-sb-link--disabled cursor-not-allowed select-none"
        title="Coming soon"
      >
        <Icon name={item.iconName} className="flex-shrink-0" />
        <span className="truncate">{item.label}</span>
        <span className="ml-auto text-[10px] uppercase tracking-wider orin-sb-ink-faint">
          Soon
        </span>
      </div>
    );
  }

  return (
    <NavLink
      to={item.to}
      data-tour={`nav-${item.id}`}
      className={({ isActive }) =>
        [
          'relative flex items-center gap-2.5 px-2.5 py-2 rounded-md text-sm font-medium',
          'transition-colors orin-sb-link',
          isActive ? 'orin-sb-link--active font-semibold' : '',
        ].join(' ')
      }
    >
      {({ isActive }) => (
        <>
          {/* Left accent strip when active */}
          {isActive && (
            <span
              className="absolute -left-3.5 top-1.5 bottom-1.5 w-[3px] rounded-r"
              style={{ background: 'var(--accent)' }}
            />
          )}
          <Icon name={item.iconName} className="flex-shrink-0" />
          <span className="truncate">{item.label}</span>
          {item.badge ? (
            <span className="ml-auto orin-sb-chip text-[11px] font-semibold px-1.5 py-0.5 rounded-full">
              {item.badge}
            </span>
          ) : null}
        </>
      )}
    </NavLink>
  );
}

/* Icons-only variant: icon stacked above a 9px label */
function NavItemIcon({ item }) {
  if (item.disabled) {
    return (
      <div
        className="flex flex-col items-center gap-1 py-2 rounded-md orin-sb-link--disabled cursor-not-allowed select-none"
        title={`${item.label} — coming soon`}
      >
        <Icon name={item.iconName} size={19} />
        <span className="text-[9px] font-medium leading-none">{item.label}</span>
      </div>
    );
  }

  return (
    <NavLink
      to={item.to}
      title={item.label}
      data-tour={`nav-${item.id}`}
      className={({ isActive }) =>
        [
          'relative flex flex-col items-center gap-1 py-2 rounded-md transition-colors orin-sb-link',
          isActive ? 'orin-sb-link--active' : '',
        ].join(' ')
      }
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span
              className="absolute -left-2 top-2 bottom-2 w-[3px] rounded-r"
              style={{ background: 'var(--accent)' }}
            />
          )}
          <span className="relative">
            <Icon name={item.iconName} size={19} />
            {item.badge ? (
              <span
                className="absolute -top-1 -right-1.5 w-2 h-2 rounded-full"
                style={{ background: 'var(--accent)' }}
                aria-hidden="true"
              />
            ) : null}
          </span>
          <span className="text-[9px] font-medium leading-none text-center px-0.5">
            {item.label}
          </span>
        </>
      )}
    </NavLink>
  );
}

export default function Sidebar({ variant = 'full' }) {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const isIcons = variant === 'icons';

  // If profile hasn't loaded yet, render a slim placeholder so the
  // grid layout doesn't jump. (RoleGuard usually prevents this case.)
  if (!profile) {
    return (
      <aside className="h-full border-r flex flex-col orin-sidebar">
        <div className="h-[60px] px-[18px] flex items-center gap-3 border-b orin-sb-border">
          <div className="w-8 h-8 rounded-md orin-sb-skeleton" />
          {!isIcons && <div className="h-3 w-20 orin-sb-skeleton rounded" />}
        </div>
      </aside>
    );
  }

  const { workspace, admin } = getNavItems(profile.role);
  const profileHref = `${getRolePrefix(profile.role)}/profile`;
  const NavItem = isIcons ? NavItemIcon : NavItemRow;

  return (
    <aside
      className="h-full min-h-0 flex flex-col border-r orin-sidebar"
      data-tour="sidebar"
    >
      {/* Brand row — matches topbar height (60px) for visual alignment */}
      <div
        className={`h-[60px] flex items-center border-b orin-sb-border flex-shrink-0 ${
          isIcons ? 'justify-center px-2' : 'gap-3 px-[18px]'
        }`}
      >
        <div
          className="w-8 h-8 rounded-md grid place-items-center text-white font-bold text-sm shadow-md flex-shrink-0"
          style={{
            background:
              'linear-gradient(135deg, var(--accent-600), var(--accent-700))',
            boxShadow:
              '0 4px 12px rgba(15,23,42,0.18), inset 0 1px 0 rgba(255,255,255,0.18)',
          }}
        >
          O
        </div>
        {!isIcons && (
          <div className="leading-tight min-w-0">
            <div className="font-bold text-[15px] orin-sb-ink tracking-tight truncate">
              {APP.NAME}
            </div>
            <div className="text-[11px] orin-sb-ink-muted font-medium">
              {APP.COMPANY_SHORT}
            </div>
          </div>
        )}
      </div>

      {/* Scrollable nav body — min-h-0 lets it shrink so overflow kicks in */}
      <nav
        className={`flex-1 min-h-0 overflow-y-auto orin-sb-nav py-3 flex flex-col gap-1 ${
          isIcons ? 'px-2' : 'px-3.5'
        }`}
      >
        {!isIcons && (
          <div className="px-2 pt-2 pb-1.5 text-[11px] font-semibold uppercase tracking-wider orin-sb-ink-faint">
            Workspace
          </div>
        )}
        {workspace.map((item) => (
          <NavItem key={item.id} item={item} />
        ))}

        {admin.length > 0 && (
          <>
            {isIcons ? (
              <div className="mx-2 my-2 h-px orin-sb-divider" aria-hidden="true" />
            ) : (
              <div className="px-2 pt-4 pb-1.5 text-[11px] font-semibold uppercase tracking-wider orin-sb-ink-faint">
                Admin
              </div>
            )}
            {admin.map((item) => (
              <NavItem key={item.id} item={item} />
            ))}
          </>
        )}
      </nav>

      {/* User block at bottom — click to open profile */}
      <button
        type="button"
        onClick={() => navigate(profileHref)}
        title={profile.full_name || 'Profile'}
        data-tour="profile"
        className={`flex items-center border-t orin-sb-border orin-sb-hover transition-colors text-left flex-shrink-0 ${
          isIcons ? 'justify-center px-2 py-3' : 'gap-2.5 px-3.5 py-3.5'
        }`}
      >
        <Avatar
          name={profile.full_name}
          photoUrl={profile.avatar_url}
          size={isIcons ? 'md' : 'lg'}
        />
        {!isIcons && (
          <>
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-[13.5px] orin-sb-ink truncate leading-tight">
                {profile.full_name || 'Unnamed user'}
              </div>
              <div className="text-xs orin-sb-ink-muted truncate mt-0.5">
                {roleLabel(profile.role)}
              </div>
            </div>
            <Icon name="chev" size={16} className="orin-sb-ink-faint" />
          </>
        )}
      </button>
    </aside>
  );
}
